import { useState, useEffect, useCallback } from 'react'
import { router } from '@inertiajs/react'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import { useOptionalInertiaProps } from '@/frontend/hooks/useOptionalInertiaProps'
import SessionSidebar from './components/SessionSidebar'
import TabContentPanel from './components/TabContentPanel'
import type { FleetSessionEvent } from '@/frontend/pages/CommandCenter/shared/ConversationView'
import type { Session, DigitalWorker, Pagination, Stats, AllowlistData, ConfigData, AvailablePlan, CurrentPlan, BillingPlanOption, WorkerSubscription, PaymentLink } from './types'
import type { KnowledgeData, SelectedFile } from '@/frontend/components/DigitalWorkers/types'
import type { CronData } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  digitalWorker: DigitalWorker
  currentPlan: CurrentPlan
  availablePlans: AvailablePlan[] | null
  billing: WorkerSubscription
  billingPlans: BillingPlanOption[]
  paymentLink: PaymentLink | null
  sessions: Session[]
  pagination: Pagination
  search: string
  channel: string
  dateFrom: string
  dateTo: string
  channels: string[]
  sortBy: string
  sortDirection: string
  stats: Stats | null
  selectedSession: Session | null
  selectedEvents: FleetSessionEvent[]
  eventPagination: Pagination
  viewMode: string
  showThinking: boolean
  showToolCalls: boolean
  eventRole: string
  knowledge?: KnowledgeData | null
  crons?: CronData | null
  allowlist?: AllowlistData | null
  telegramAllowlist?: AllowlistData | null
  config?: ConfigData | null
}

type Tab = 'sessoes' | 'rotinas' | 'memoria' | 'allowlist' | 'config'

function initialTabFromUrl(): Tab {
  if (typeof window === 'undefined') return 'sessoes'
  const tab = new URLSearchParams(window.location.search).get('tab')
  if (tab === 'rotinas' || tab === 'memoria' || tab === 'allowlist' || tab === 'config') return tab
  return 'sessoes'
}

function DigitalWorkerShow(props: Props) {
  const {
    digitalWorker, currentPlan, availablePlans, sessions, pagination, search, channel,
    dateFrom, dateTo, channels, sortBy, sortDirection, stats, selectedSession,
    selectedEvents, eventPagination, viewMode, showThinking, showToolCalls, eventRole,
    knowledge, crons, allowlist, telegramAllowlist, config, billing, billingPlans, paymentLink,
  } = props

  const { loadProps } = useOptionalInertiaProps()
  const [activeTab, setActiveTab] = useState<Tab>(initialTabFromUrl)
  const [memorySearch, setMemorySearch] = useState('')
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null)
  const isReady = digitalWorker.provisioningStatus === 'ready'
  const basePath = `/cc/digital_workers/${digitalWorker.id}`

  const handleTabChange = useCallback((tab: Tab) => {
    setActiveTab(tab)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.set('tab', tab)
      window.history.replaceState({}, '', url.toString())
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'rotinas') loadProps(['crons'], isReady)
    if (activeTab === 'memoria') loadProps(['knowledge'], isReady)
    if (activeTab === 'allowlist') loadProps(['allowlist', 'telegramAllowlist'], isReady && (digitalWorker.runtimeCapabilities.whatsapp_cloud.supported || digitalWorker.runtimeCapabilities.gateway_channels.supported))
    if (activeTab === 'config') loadProps(['config'], isReady)
  }, [activeTab, isReady, loadProps, digitalWorker.runtimeCapabilities.gateway_channels.supported, digitalWorker.runtimeCapabilities.whatsapp_cloud.supported])

  const currentParams: Record<string, string | number | undefined> = {
    search: search || undefined, channel: channel || undefined,
    date_from: dateFrom || undefined, date_to: dateTo || undefined,
    sort_by: sortBy !== 'last_event_at' ? sortBy : undefined,
    sort_direction: sortDirection !== 'desc' ? sortDirection : undefined,
    page: pagination.page > 1 ? pagination.page : undefined,
    session_id: selectedSession?.sessionId,
    event_page: eventPagination.page > 1 ? eventPagination.page : undefined,
    view_mode: viewMode !== 'conversation' ? viewMode : undefined,
    show_thinking: showThinking ? undefined : 'false',
    show_tool_calls: showToolCalls ? undefined : 'false',
    event_role: eventRole || undefined,
  }

  const handleSessionClick = (sessionId: string) => {
    const params = { ...currentParams, session_id: sessionId, event_page: undefined }
    const cleanParams = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    router.get(basePath, cleanParams, { preserveState: true, preserveScroll: true, replace: true })
  }

  return (
    <div className="flex h-full">
      <SessionSidebar
        digitalWorker={digitalWorker} stats={stats} sessions={sessions} pagination={pagination}
        selectedSessionId={selectedSession?.sessionId ?? null}
        search={search} channel={channel} dateFrom={dateFrom} dateTo={dateTo}
        channels={channels} basePath={basePath} currentParams={currentParams}
        onSessionClick={handleSessionClick}
        activeTab={activeTab} onTabChange={handleTabChange}
        memorySearch={memorySearch} onMemorySearchChange={setMemorySearch}
        knowledge={knowledge ?? null} onSelectFile={setSelectedFile}
        config={config ?? null}
        currentPlan={currentPlan} availablePlans={availablePlans}
        billing={billing} billingPlans={billingPlans} paymentLink={paymentLink}
      />
      <TabContentPanel
        activeTab={activeTab} selectedFile={selectedFile} selectedSession={selectedSession}
        selectedEvents={selectedEvents} viewMode={viewMode} showThinking={showThinking}
        showToolCalls={showToolCalls} eventRole={eventRole} basePath={basePath}
        currentParams={currentParams} knowledge={knowledge} crons={crons}
        allowlist={allowlist} telegramAllowlist={telegramAllowlist} config={config}
        digitalWorker={digitalWorker}
        fileContentUrl={`${basePath}/knowledge_file`}
      />
    </div>
  )
}

DigitalWorkerShow.layout = (page: React.ReactElement) => <CommandCenterLayout variant="fullscreen">{page}</CommandCenterLayout>

export default DigitalWorkerShow
